import { useState, useEffect, useCallback } from 'react';
import {
  MagnifyingGlassIcon,
  UserCircleIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { createUserAsync } from '../store/usersSlice';
import { User } from '../types';
import { peopleApi, AzureAdUserSearchResult } from '../services/api';
import Modal from './Modal';
import Button from './Button';

interface AzureAdUserSearchModalForUsersProps {
  isOpen: boolean;
  onClose: () => void;
  onUserAdded?: (user: User) => void;
}

export default function AzureAdUserSearchModalForUsers({
  isOpen,
  onClose,
  onUserAdded,
}: AzureAdUserSearchModalForUsersProps) {
  const dispatch = useAppDispatch();
  const locations = useAppSelector((state) => state.locations.locations);
  const existingUsers = useAppSelector((state) => state.users.users);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<AzureAdUserSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<AzureAdUserSearchResult | null>(null);
  const [selectedRole, setSelectedRole] = useState<'admin' | 'site-admin' | 'key-requester' | 'user'>('user');
  const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>([]);
  const [isAdding, setIsAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSearchQuery('');
      setSearchResults([]);
      setSearchError(null);
      setSelectedUser(null);
      setSelectedRole('user');
      setSelectedLocationIds([]);
      setAddError(null);
    }
  }, [isOpen]);

  const handleSearch = useCallback(async () => {
    if (!searchQuery || searchQuery.trim().length < 2) {
      setSearchError('Please enter at least 2 characters');
      return;
    }

    setIsSearching(true);
    setSearchError(null);
    setSelectedUser(null);

    try {
      const results = await peopleApi.searchAzureAd(searchQuery.trim());
      setSearchResults(results);

      if (results.length === 0) {
        setSearchError('No users found matching your search');
      }
    } catch (error) {
      console.error('Search error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to search Azure AD';
      setSearchError(errorMessage);
    } finally {
      setIsSearching(false);
    }
  }, [searchQuery]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleSelectUser = (user: AzureAdUserSearchResult) => {
    setSelectedUser(user);
    setAddError(null);
  };

  const isUserAlreadyAdded = (email: string): boolean => {
    return existingUsers.some((u) => u.email.toLowerCase() === email.toLowerCase());
  };

  const handleLocationToggle = (locationId: string) => {
    setSelectedLocationIds(prev => 
      prev.includes(locationId)
        ? prev.filter(id => id !== locationId)
        : [...prev, locationId]
    );
  };

  const handleAddUser = async () => {
    if (!selectedUser) return;

    // Check if already exists
    if (isUserAlreadyAdded(selectedUser.email)) {
      setAddError('A user with this email already exists in the system');
      return;
    }

    // Validate role selection
    if (!selectedRole) {
      setAddError('Please select a role');
      return;
    }

    // For site-admin and user roles, validate location selection (admins and key-requesters get all locations)
    if ((selectedRole === 'site-admin' || selectedRole === 'user') && selectedLocationIds.length === 0) {
      setAddError('Please select at least one location');
      return;
    }

    setIsAdding(true);
    setAddError(null);

    try {
      // For admin and key-requester, grant access to all locations
      const locationAccess = (selectedRole === 'admin' || selectedRole === 'key-requester')
        ? locations.map(loc => loc.id)
        : selectedLocationIds;

      const newUser: User = {
        id: `user-${Date.now()}`,
        name: selectedUser.displayName,
        email: selectedUser.email,
        role: selectedRole,
        locationAccess,
        photoUrl: selectedUser.photoUrl,
      };

      const result = await dispatch(createUserAsync(newUser)).unwrap();
      onUserAdded?.(result);
      onClose();
    } catch (error) {
      console.error('Failed to add user:', error);
      setAddError(error instanceof Error ? error.message : 'Failed to add user');
    } finally {
      setIsAdding(false);
    }
  };

  const canAdd = selectedUser && 
    !isUserAlreadyAdded(selectedUser?.email || '') && 
    selectedRole &&
    (selectedRole === 'admin' || selectedRole === 'key-requester' || selectedLocationIds.length > 0);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add User from Azure AD"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isAdding}>
            Cancel
          </Button>
          <Button
            onClick={handleAddUser}
            disabled={!canAdd || isAdding}
          >
            {isAdding ? 'Adding...' : 'Add User'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/* Search Input */}
        <div>
          <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
            Search Azure AD Users
          </label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search by name or email..."
                className="w-full pl-10 pr-4 py-2 border rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100 placeholder-secondary-400 dark:placeholder-secondary-500 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                disabled={isSearching}
              />
              <MagnifyingGlassIcon className="absolute left-3 top-2.5 w-5 h-5 text-secondary-400 dark:text-secondary-500" />
            </div>
            <Button onClick={handleSearch} disabled={isSearching || !searchQuery.trim()}>
              {isSearching ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                'Search'
              )}
            </Button>
          </div>
        </div>

        {/* Search Error */}
        {searchError && (
          <div className="p-3 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <p className="text-sm text-yellow-700 dark:text-yellow-300">{searchError}</p>
          </div>
        )}

        {/* Search Results */}
        {searchResults.length > 0 && (
          <div className="border border-secondary-200 dark:border-secondary-600 rounded-lg overflow-hidden">
            <div className="max-h-48 overflow-y-auto">
              {searchResults.map((user) => {
                const alreadyAdded = isUserAlreadyAdded(user.email);
                const isSelected = selectedUser?.id === user.id;

                return (
                  <button
                    key={user.id}
                    onClick={() => !alreadyAdded && handleSelectUser(user)}
                    disabled={alreadyAdded}
                    className={`w-full flex items-center p-3 text-left border-b border-secondary-100 dark:border-secondary-700 last:border-b-0 transition-colors ${
                      alreadyAdded
                        ? 'bg-secondary-50 dark:bg-secondary-800 cursor-not-allowed opacity-60'
                        : isSelected
                        ? 'bg-primary-50 dark:bg-primary-900/30 border-l-4 border-l-primary-500'
                        : 'hover:bg-secondary-50 dark:hover:bg-secondary-700'
                    }`}
                  >
                    {user.photoUrl ? (
                      <img
                        src={user.photoUrl}
                        alt={user.displayName}
                        className="w-10 h-10 rounded-full mr-3 flex-shrink-0 object-cover"
                      />
                    ) : (
                      <UserCircleIcon className="w-10 h-10 text-secondary-400 dark:text-secondary-500 mr-3 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-secondary-900 dark:text-secondary-100 truncate">
                        {user.displayName}
                      </p>
                      <p className="text-xs text-secondary-500 dark:text-secondary-400 truncate">{user.email}</p>
                      {user.department && (
                        <p className="text-xs text-secondary-400 dark:text-secondary-500">{user.department}</p>
                      )}
                    </div>
                    {alreadyAdded ? (
                      <span className="text-xs text-secondary-500 dark:text-secondary-400 bg-secondary-100 dark:bg-secondary-700 px-2 py-1 rounded">
                        Already added
                      </span>
                    ) : isSelected ? (
                      <CheckIcon className="w-5 h-5 text-primary-600 dark:text-primary-400 flex-shrink-0" />
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Selected User Details & Role Selection */}
        {selectedUser && !isUserAlreadyAdded(selectedUser.email) && (
          <div className="p-4 bg-primary-50 dark:bg-primary-900/30 border border-primary-200 dark:border-primary-800 rounded-lg">
            <h4 className="text-sm font-medium text-secondary-900 dark:text-secondary-100 mb-3">
              Selected User
            </h4>
            <dl className="space-y-1 text-sm">
              <div className="flex">
                <dt className="w-24 text-secondary-600 dark:text-secondary-400">Name:</dt>
                <dd className="text-secondary-900 dark:text-secondary-100">{selectedUser.displayName}</dd>
              </div>
              <div className="flex">
                <dt className="w-24 text-secondary-600 dark:text-secondary-400">Email:</dt>
                <dd className="text-secondary-900 dark:text-secondary-100">{selectedUser.email}</dd>
              </div>
              {selectedUser.department && (
                <div className="flex">
                  <dt className="w-24 text-secondary-600 dark:text-secondary-400">Department:</dt>
                  <dd className="text-secondary-900 dark:text-secondary-100">{selectedUser.department}</dd>
                </div>
              )}
            </dl>

            {/* Role Selection */}
            <div className="mt-4 pt-4 border-t border-primary-200 dark:border-primary-700">
              <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                Assign Role *
              </label>
              <select
                value={selectedRole}
                onChange={(e) => {
                  setSelectedRole(e.target.value as 'admin' | 'site-admin' | 'key-requester' | 'user');
                  // Clear location selection when switching to roles that don't need it
                  if (e.target.value === 'admin' || e.target.value === 'key-requester') {
                    setSelectedLocationIds([]);
                  }
                }}
                className="w-full px-3 py-2 border rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="user">User (Read-only)</option>
                <option value="site-admin">Site Admin (Location-specific)</option>
                <option value="key-requester">Key Requester (HR - Request keys)</option>
                <option value="admin">Admin (Full Access)</option>
              </select>
            </div>

            {/* Location Selection for Site Admin and User roles */}
            {(selectedRole === 'site-admin' || selectedRole === 'user') && (
              <div className="mt-4 pt-4 border-t border-primary-200 dark:border-primary-700">
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                  Location Access *
                </label>
                <div className="space-y-2 max-h-32 overflow-y-auto border border-secondary-300 dark:border-secondary-600 rounded-lg p-3 bg-white dark:bg-secondary-700">
                  {locations.map((location) => (
                    <label key={location.id} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedLocationIds.includes(location.id)}
                        onChange={() => handleLocationToggle(location.id)}
                        className="w-4 h-4 text-primary-600 border-secondary-300 dark:border-secondary-600 rounded focus:ring-primary-500 bg-white dark:bg-secondary-600"
                      />
                      <span className="ml-2 text-sm text-secondary-900 dark:text-secondary-100">
                        {location.name}
                      </span>
                    </label>
                  ))}
                </div>
                {selectedLocationIds.length === 0 && (
                  <p className="mt-1 text-xs text-warning dark:text-yellow-400">
                    Please select at least one location
                  </p>
                )}
              </div>
            )}

            {/* Admin Info */}
            {selectedRole === 'admin' && (
              <div className="mt-4 p-3 bg-primary-100 dark:bg-primary-900/50 rounded-lg">
                <p className="text-sm text-primary-800 dark:text-primary-200">
                  Admins have access to all locations and full system permissions.
                </p>
              </div>
            )}

            {/* Key Requester Info */}
            {selectedRole === 'key-requester' && (
              <div className="mt-4 p-3 bg-amber-100 dark:bg-amber-900/50 rounded-lg">
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  Key Requesters (HR) can request keys for any location. Requests require approval from site admins before keys are assigned.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Add Error */}
        {addError && (
          <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-700 dark:text-red-300">{addError}</p>
          </div>
        )}
      </div>
    </Modal>
  );
}
